/**
 * Lightweight Auth Session & Token Extraction Utility
 * Handles client-side extraction of JWT payload, user privileges, and active modules.
 */

export function parseStoredUser() {
  const raw = localStorage.getItem('user')
  const tokenRaw = localStorage.getItem('authToken')
  let tokenPrivileges = []
  let tokenIsAdmin = false
  let tokenModules = []
  let tokenPlatformRole = null
  let tokenUserId = null
  let tokenTenantId = null
  let tokenRole = null
  let tokenIsEmployee = false
  let tokenName = null
  let tokenEmail = null

  if (tokenRaw) {
    try {
      const [, payloadPart = ''] = String(tokenRaw).split('.')
      if (payloadPart) {
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
        const padding = '='.repeat((4 - (normalized.length % 4)) % 4)
        const decoded = atob(normalized + padding)
        const payload = JSON.parse(decoded)
        tokenPrivileges = Array.isArray(payload?.privileges)
          ? payload.privileges.map((item) => String(item || '').trim()).filter(Boolean)
          : []
        tokenIsAdmin = Boolean(payload?.is_admin ?? payload?.isAdmin)
        tokenModules = Array.isArray(payload?.modules)
          ? payload.modules.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean)
          : []
        tokenPlatformRole = payload?.platform_role ?? payload?.platformRole ?? null
        tokenUserId = payload?.user_id ?? payload?.userId ?? null
        tokenTenantId = payload?.tenant_id ?? payload?.tenantId ?? null
        tokenRole = payload?.role ?? null
        tokenIsEmployee = Boolean(payload?.is_employee ?? (payload?.role === 'employee'))
        tokenName = payload?.name || null
        tokenEmail = payload?.email || null
      }
    } catch {
      tokenPrivileges = []
      tokenIsAdmin = false
      tokenModules = []
      tokenPlatformRole = null
      tokenUserId = null
      tokenTenantId = null
      tokenRole = null
      tokenIsEmployee = false
      tokenName = null
      tokenEmail = null
    }
  }

  const fallbackTokenUserId = Number(tokenUserId) || null

  if (!raw) {
    return {
      id: fallbackTokenUserId,
      userId: fallbackTokenUserId,
      name: tokenName || '',
      email: tokenEmail || '',
      employee_id: null,
      designation: '',
      avatarUrl: '',
      tenantId: tokenTenantId,
      isAdmin: tokenIsAdmin,
      platformRole: tokenPlatformRole,
      role: tokenRole,
      is_employee: tokenIsEmployee,
      privileges: tokenPrivileges,
      modules: tokenModules,
    }
  }

  try {
    const parsed = JSON.parse(raw)
    const userPrivileges = Array.isArray(parsed?.privileges)
      ? parsed.privileges.map((item) => String(item || '').trim()).filter(Boolean)
      : []
    const userModules = Array.isArray(parsed?.modules)
      ? parsed.modules.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean)
      : tokenModules
    const rawUserId = Number(parsed?.id ?? parsed?.user_id ?? parsed?.userId ?? tokenUserId) || fallbackTokenUserId
    const rawName = String(parsed?.name || parsed?.userName || tokenName || '').trim()
    const rawEmail = String(parsed?.email || tokenEmail || '').trim()
    const rawEmployeeId = parsed?.employee_id || parsed?.employeeId || null
    const rawDesignation = parsed?.designation || parsed?.roleName || parsed?.role || ''
    const rawProfilePhoto =
      parsed?.profile_photo_path ||
      parsed?.profilePhotoPath ||
      parsed?.profilePhotoUrl ||
      parsed?.avatar ||
      ''

    return {
      id: rawUserId,
      userId: rawUserId,
      name: rawName,
      email: rawEmail,
      employee_id: rawEmployeeId,
      designation: rawDesignation,
      avatarUrl: rawProfilePhoto,
      tenantId: parsed?.tenant_id ?? parsed?.tenantId ?? tokenTenantId,
      isAdmin: Boolean(parsed?.is_admin ?? parsed?.isAdmin ?? tokenIsAdmin),
      platformRole: parsed?.platform_role ?? parsed?.platformRole ?? tokenPlatformRole,
      role: parsed?.role ?? tokenRole,
      is_employee: Boolean(parsed?.is_employee ?? (parsed?.role === 'employee') ?? tokenIsEmployee),
      privileges: Array.from(new Set([...userPrivileges, ...tokenPrivileges])),
      modules: userModules,
    }
  } catch {
    return {
      id: fallbackTokenUserId,
      userId: fallbackTokenUserId,
      name: tokenName || '',
      email: tokenEmail || '',
      employee_id: null,
      designation: '',
      avatarUrl: '',
      tenantId: tokenTenantId,
      isAdmin: tokenIsAdmin,
      platformRole: tokenPlatformRole,
      role: tokenRole,
      is_employee: tokenIsEmployee,
      privileges: tokenPrivileges,
      modules: tokenModules,
    }
  }
}

/**
 * Standardized accessor for the currently logged in user context.
 */
export function getCurrentUser() {
  return parseStoredUser()
}

/**
 * Standardized accessor for the current user's numeric ID.
 */
export function getCurrentUserId() {
  const user = parseStoredUser()
  return user?.id || user?.userId || null
}

export function getStoredTenantUsername() {
  const tokenRaw = localStorage.getItem('authToken')
  if (tokenRaw) {
    try {
      const [, payloadPart = ''] = String(tokenRaw).split('.')
      if (payloadPart) {
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
        const padding = '='.repeat((4 - (normalized.length % 4)) % 4)
        const decoded = atob(normalized + padding)
        const payload = JSON.parse(decoded)
        const tenant = payload?.tenant_username || payload?.tenantUsername
        if (tenant) return String(tenant).trim().toUpperCase()
      }
    } catch {
      // ignore
    }
  }

  const remembered = localStorage.getItem('rememberTenantUsername')
  if (remembered) return String(remembered).trim().toUpperCase()

  const rawUser = localStorage.getItem('user')
  if (rawUser) {
    try {
      const user = JSON.parse(rawUser)
      const tenant = user?.tenant_username || user?.tenantUsername
      if (tenant) return String(tenant).trim().toUpperCase()
    } catch {
      // ignore
    }
  }

  return ''
}

export function getRequiredModuleForPath(pathname) {
  const segments = String(pathname || '/').toLowerCase().split('/').filter(Boolean)
  const root = segments[0] || ''
  const sub = segments[1] || ''

  if (root === 'payroll') return 'payroll'
  if (root === 'lectures' || root === 'attendance' || root === 'remarks' || root === 'cloud' || root === 'admission') return 'education'
  if (root === 'masters' && [
    'place', 'school-college', 'classroom',
    'courses', 'batches', 'subjects', 'schemes', 'medium', 'boards',
    'inventory', 'brand', 'boe-cha', 'boe-forwarder', 'acheads', 'payment-mode',
    'relations', 'occupations', 'caste', 'sub-caste',
    'remarks', 'remarks-category', 'contact-category',
  ].includes(sub)) return 'education'

  if (root === 'productivity' || root === 'bruno') return 'bruno'
  if (root === 'dashboard' && sub === 'calendar') return 'bruno'
  if (root === 'reports' && sub === 'productivity') return 'bruno'
  if (root === 'approval' && (sub === 'leave' || sub === 'productivity-hours')) return 'bruno'

  if (root === 'project-management' || root === 'nerve' || root === 'allocation') return 'nerve'
  if (root === 'dashboard' && (sub === 'financial' || sub === 'team')) return 'nerve'
  if (root === 'reports' && ['financial', 'project', 'task', 'integration', 'user'].includes(sub)) return 'nerve'
  if (root === 'approval' && sub === 'timesheet') return 'nerve'
  if (root === 'masters' && ['task-master', 'job-master', 'currency'].includes(sub)) return 'nerve'
  if (root === 'analytics' && ['project', 'task', 'job'].includes(sub)) return 'nerve'

  if (root === 'communication' || root === 'bridge') return 'bridge'
  if (root === 'reports' && sub === 'api-logs') return 'bridge'
  if (root === 'telephony' || root === 'loop') return 'loop'

  return null
}

export function getModuleDisplayName(moduleName) {
  const NAMES = {
    education: 'Education',
    payroll: 'Payroll',
    bruno: 'Bruno',
    nerve: 'Nerve',
    bridge: 'Bridge',
    loop: 'Loop',
    telephony: 'Loop',
    pulse: 'Pulse CRM',
    analytics: 'Analytics',
    reports: 'Reports',
    audit_logs: 'Audit Logs',
  }
  return NAMES[String(moduleName || '').toLowerCase()] || moduleName
}

export function isPermissionDeniedError(error) {
  const status = Number(error?.response?.status || 0)
  if (status !== 403) return false

  const detail = error?.response?.data?.detail
  const message = String(
    (typeof detail === 'object' ? detail?.message : detail)
      || error?.response?.statusText
      || error?.message
      || '',
  ).toLowerCase()
  const code = String((typeof detail === 'object' ? detail?.code : '') || '').toLowerCase()

  if (code.includes('permission') || code.includes('forbidden')) return true
  return message.includes('permission denied') || message.includes('forbidden') || message.includes('not authorized')
}
